<template>
  <ul class="organization-tree" :class="{ nested: level > 0 }" role="tree">
    <li v-for="node in nodes" :key="nodeKey(node)" role="treeitem" :aria-expanded="nodeExpandedState(node)">
      <article
        class="tree-node"
        :class="[`type-${node.type}`, { interactive: node.type === 'function' }]"
        :role="node.type === 'function' ? 'button' : undefined"
        :tabindex="node.type === 'function' ? 0 : undefined"
        :aria-controls="node.type === 'function' ? peoplePanelId(node) : undefined"
        @click="handleNodeClick(node)"
        @keydown.enter.prevent="handleNodeClick(node)"
        @keydown.space.prevent="handleNodeClick(node)"
      >
        <button
          v-if="hasChildren(node)"
          type="button"
          class="toggle"
          :aria-label="`${isExpanded(node) ? 'Recolher' : 'Expandir'} ${node.name}`"
          @click="toggle(node)"
        >
          <span aria-hidden="true">{{ isExpanded(node) ? '−' : '+' }}</span>
        </button>

        <div class="node-content">
          <div class="node-heading">
            <div>
              <span class="type-label">{{ typeLabel(node) }}</span>
              <strong>{{ node.name }}</strong>
              <code v-if="node.code">{{ node.code }}</code>
            </div>
            <span class="status"><span aria-hidden="true" />Ativo</span>
          </div>

          <dl class="node-metrics">
            <div v-if="node.type !== 'function'">
              <dt>Funções</dt>
              <dd>{{ node.counts?.functions || 0 }}</dd>
            </div>
            <div>
              <dt>Pessoas ativas</dt>
              <dd>{{ node.counts?.activeCollaborators || 0 }}</dd>
            </div>
          </dl>

          <dl v-if="hasGoogleGroups(node)" class="node-groups" aria-label="Grupos Google">
            <div v-if="node.hierarchicalGroupEmail">
              <dt>Grupo hierárquico</dt>
              <dd>{{ node.hierarchicalGroupEmail }}</dd>
            </div>
            <div v-if="node.communicationGroupEmail">
              <dt>Grupo de comunicação</dt>
              <dd>{{ node.communicationGroupEmail }}</dd>
            </div>
          </dl>

          <div v-if="node.responsibleFunctions?.length" class="responsibilities">
            <span>Responsável</span>
            <ul>
              <li v-for="responsible in node.responsibleFunctions" :key="responsible.id">
                {{ responsible.name }}
              </li>
            </ul>
          </div>
        </div>
      </article>

      <section
        v-if="node.type === 'function' && isPeopleExpanded(node)"
        :id="peoplePanelId(node)"
        class="people-panel"
        :aria-label="`Pessoas ativas em ${node.name}`"
        aria-live="polite"
      >
        <p v-if="isPeopleLoading(node)" class="people-state">Carregando pessoas ativas...</p>
        <div v-else-if="peopleError(node)" class="people-state people-error" role="alert">
          <p>{{ peopleError(node) }}</p>
          <button type="button" class="btn btn-secondary btn-small" @click.stop="loadPeople(node, true)">
            Tentar novamente
          </button>
        </div>
        <template v-else>
          <header>
            <strong>Pessoas ativas</strong>
            <span>{{ peopleFor(node).length }}</span>
          </header>
          <p v-if="!peopleFor(node).length" class="people-state">Nenhuma pessoa ativa vinculada a esta função.</p>
          <ul v-else>
            <li v-for="person in peopleFor(node)" :key="person.id">
              {{ person.name }}
            </li>
          </ul>
        </template>
      </section>

      <organization-tree
        v-if="hasChildren(node) && isExpanded(node)"
        :nodes="node.children"
        :level="level + 1"
      />
    </li>
  </ul>
</template>

<script>
import OrganizationalStructureService from '@/services/organizational-structure.service'

export default {
  name: 'OrganizationTree',
  props: {
    nodes: { type: Array, required: true },
    level: { type: Number, default: 0 }
  },
  data () {
    return {
      expanded: new Set(),
      expandedPeople: new Set(),
      peopleByFunction: {},
      loadingPeople: new Set(),
      peopleErrors: {}
    }
  },
  methods: {
    nodeKey (node) {
      return `${node.type}-${node.id}`
    },
    hasChildren (node) {
      return Boolean(node.children?.length)
    },
    isExpanded (node) {
      return this.expanded.has(this.nodeKey(node))
    },
    nodeExpandedState (node) {
      if (this.hasChildren(node)) return this.isExpanded(node)
      if (node.type === 'function') return this.isPeopleExpanded(node)
      return undefined
    },
    toggle (node) {
      const key = this.nodeKey(node)
      const next = new Set(this.expanded)
      next.has(key) ? next.delete(key) : next.add(key)
      this.expanded = next
    },
    handleNodeClick (node) {
      if (node.type !== 'function') return
      const key = this.nodeKey(node)
      const next = new Set(this.expandedPeople)
      if (next.has(key)) {
        next.delete(key)
        this.expandedPeople = next
        return
      }
      next.add(key)
      this.expandedPeople = next
      if (!Object.prototype.hasOwnProperty.call(this.peopleByFunction, key)) {
        this.loadPeople(node)
      }
    },
    async loadPeople (node, force = false) {
      const key = this.nodeKey(node)
      if (this.loadingPeople.has(key) || (!force && Object.prototype.hasOwnProperty.call(this.peopleByFunction, key))) return
      const loading = new Set(this.loadingPeople)
      loading.add(key)
      this.loadingPeople = loading
      this.peopleErrors = { ...this.peopleErrors, [key]: '' }
      try {
        const response = await OrganizationalStructureService.activePeople(node.id)
        this.peopleByFunction = {
          ...this.peopleByFunction,
          [key]: response.data.data?.people || []
        }
      } catch (error) {
        this.peopleErrors = {
          ...this.peopleErrors,
          [key]: error.response?.data?.message || 'Não foi possível carregar as pessoas ativas.'
        }
      } finally {
        const nextLoading = new Set(this.loadingPeople)
        nextLoading.delete(key)
        this.loadingPeople = nextLoading
      }
    },
    isPeopleExpanded (node) {
      return this.expandedPeople.has(this.nodeKey(node))
    },
    isPeopleLoading (node) {
      return this.loadingPeople.has(this.nodeKey(node))
    },
    peopleFor (node) {
      return this.peopleByFunction[this.nodeKey(node)] || []
    },
    peopleError (node) {
      return this.peopleErrors[this.nodeKey(node)] || ''
    },
    peoplePanelId (node) {
      return `active-people-${node.id}`
    },
    hasGoogleGroups (node) {
      return Boolean(node.hierarchicalGroupEmail || node.communicationGroupEmail)
    },
    typeLabel (node) {
      return {
        department: 'Departamento',
        area: 'Área',
        subarea: 'Subárea',
        function: 'Função'
      }[node.type] || node.type
    }
  }
}
</script>

<style lang="scss" scoped>
.organization-tree {
  display: grid;
  gap: $hmg_small;
  margin: 0;
  padding: 0;
  list-style: none;
}
.organization-tree.nested {
  position: relative;
  margin: $hmg_small 0 0 $mg_mid;
  padding-left: $mg_small;
  border-left: $border-component;
}
.tree-node {
  display: flex;
  align-items: flex-start;
  gap: $mg_mini;
  min-width: 0;
  padding: $hmg_small;
  border: $border-component;
  border-radius: $border-radius;
  background: $color-secondary;
}
.tree-node.type-department { border-left: 4px solid $color-primary !important; }
.tree-node.type-area { border-left: 4px solid $color-view !important; }
.tree-node.type-subarea { border-left: 4px solid $color-success !important; }
.tree-node.type-function { border-left: 4px solid $color-component !important; }
.tree-node.interactive { cursor: pointer; }
.tree-node.interactive:hover { border-color: $color-fade-low; background: $color-bg; }
.tree-node.interactive:focus-visible { outline: 3px solid $color-success; outline-offset: 2px; }
.toggle {
  display: inline-flex;
  width: 32px;
  min-width: 32px;
  height: 32px;
  align-items: center;
  justify-content: center;
  margin-top: 2px;
  border: 0;
  border-radius: 50%;
  background: $color-bg;
  color: $color-primary;
  font-size: 20px;
  font-weight: 700;
  cursor: pointer;
}
.toggle:focus-visible { outline: 3px solid $color-success; outline-offset: 2px; }
.node-content { flex: 1; min-width: 0; }
.node-heading { display: flex; align-items: flex-start; justify-content: space-between; gap: $mg_small; }
.node-heading > div { display: flex; flex-wrap: wrap; align-items: baseline; gap: $mg_mini; }
.node-heading strong { font-size: 16px; color: $color-dev; }
.node-heading code { color: $color-text; font-family: $font-code; font-size: $font-size-small; }
.type-label { color: $color-text; font-size: $font-size-small; font-weight: 700; }
.status { display: inline-flex; align-items: center; gap: $hmg_mini; font-size: $font-size-small; font-weight: 700; }
.status > span { width: 8px; height: 8px; border-radius: 50%; background: $color-success-2; }
.node-metrics { display: flex; flex-wrap: wrap; gap: $mg_small; margin: $mg_mini 0 0; }
.node-metrics div { display: flex; gap: $hmg_mini; }
.node-metrics dt { color: $color-text; }
.node-metrics dd { margin: 0; font-weight: 700; }
.node-groups { display: flex; flex-wrap: wrap; gap: $hmg_mini $mg_small; margin: $mg_mini 0 0; }
.node-groups div { display: flex; flex: 1 1 260px; flex-wrap: wrap; gap: $hmg_mini; min-width: 0; }
.node-groups dt { color: $color-text; font-weight: 600; }
.node-groups dd { min-width: 0; margin: 0; color: $color-dev; font-weight: 600; overflow-wrap: anywhere; }
.responsibilities { display: flex; flex-wrap: wrap; gap: $hmg_mini; margin-top: $mg_mini; }
.responsibilities > span { color: $color-text; }
.responsibilities ul { display: flex; flex-wrap: wrap; gap: $hmg_mini; margin: 0; padding: 0; list-style: none; }
.responsibilities li { padding: $hmg_mini $mg_mini; border-radius: $border-radius-xlarge; background: $color-bg; font-weight: 600; }
.people-panel { margin: $hmg_small 0 0 $mg_mid; padding: $mg_small; border: $border-component; border-radius: $border-radius; background: $color-bg; }
.people-panel header { display: flex; align-items: center; justify-content: space-between; gap: $mg_small; margin-bottom: $mg_mini; }
.people-panel header span { min-width: 28px; padding: $hmg_mini $mg_mini; border-radius: $border-radius-xlarge; background: $color-secondary; text-align: center; font-weight: 700; }
.people-panel ul { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: $hmg_mini $mg_small; margin: 0; padding: 0; list-style: none; }
.people-panel li { min-width: 0; padding: $mg_mini $mg_small; border-left: 3px solid $color-component; border-radius: $border-radius-mid; background: $color-secondary; font-weight: 600; overflow-wrap: anywhere; }
.people-state { margin: 0; color: $color-text; }
.people-error { display: flex; align-items: center; justify-content: space-between; gap: $mg_small; }
.people-error p { margin: 0; }
@media (max-width: 640px) {
  .organization-tree.nested { margin-left: $mg_mini; padding-left: $mg_mini; }
  .tree-node { padding: $mg_mini; }
  .node-heading { flex-direction: column; gap: $hmg_mini; }
  .toggle { width: 40px; min-width: 40px; height: 40px; }
  .people-panel { margin-left: $mg_mini; }
  .people-panel ul { grid-template-columns: 1fr; }
  .people-error { align-items: stretch; flex-direction: column; }
}
@media (prefers-reduced-motion: reduce) {
  .toggle { transition: none; }
}
</style>
